import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Gift, Send } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function PublicInvite() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    referee_name: params.get("n") || "",
    referee_business: params.get("b") || "",
    referee_phone: params.get("p") || "",
    referee_email: "",
    referee_role: "",
    note: "",
  });

  useEffect(() => {
    api.get(`/public/invite/${code}`).then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e)));
  }, [code]);

  const money = useMemo(() => {
    const cur = data?.settings?.currency || "R";
    return (n) => `${cur} ${Number(n || 0).toLocaleString()}`;
  }, [data]);

  const submit = async () => {
    if (!form.referee_name.trim() || !form.referee_business.trim()) {
      toast.error("Please share your name and business.");
      return;
    }
    if (!form.referee_phone.trim() && !form.referee_email.trim()) {
      toast.error("Add a phone number or email so the team can reach you.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/public/invite/${code}`, {
        referee_name: form.referee_name.trim(),
        referee_business: form.referee_business.trim(),
        referee_phone: form.referee_phone.trim() || null,
        referee_email: form.referee_email.trim() || null,
        referee_role: form.referee_role.trim() || null,
        note: form.note.trim() || null,
      });
      setDone(true);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
          <h1 className="font-head text-xl font-bold text-slate-900">Invite not found</h1>
          <p className="mt-2 text-sm text-slate-500">This invite link isn't active any more. Ask your friend for a fresh one.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="flex min-h-screen items-center justify-center text-slate-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading your invite…</div>;
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-amber-50 p-6">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-lg" data-testid="invite-done">
          <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-emerald-500" />
          <h1 className="font-head text-2xl font-bold text-slate-900">You're in!</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Thanks for taking the first step. The Servantage team will reach out to {form.referee_name.split(" ")[0] || "you"} very soon to set up a short, no-pressure demo. {data.employee.name?.split(" ")[0] || "Your friend"} will be rewarded when you come on board — and so will you.
          </p>
        </div>
      </div>
    );
  }

  const emp = data.employee;
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-amber-50 p-4 sm:p-6" data-testid="invite-page">
      <div className="mx-auto flex max-w-md flex-col">
        {/* Attribution header */}
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            {emp.photo_url ? (
              <img src={emp.photo_url} alt={emp.name} className="h-14 w-14 flex-none rounded-full object-cover" />
            ) : (
              <div className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-indigo-100 text-xl font-bold text-indigo-600">
                {(emp.name || "?").charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-wide text-indigo-500">You're invited by</div>
              <div className="font-head text-lg font-bold text-slate-900">{emp.name}</div>
              <div className="text-xs text-slate-500">
                {emp.job_title && <>{emp.job_title} · </>}
                {data.organization?.name}
                {data.location_name && <> · {data.location_name}</>}
              </div>
            </div>
          </div>
        </div>

        {/* Reward hero */}
        <div className="mt-3 rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-5 text-center">
          <Gift className="mx-auto mb-2 h-6 w-6 text-amber-500" />
          <div className="font-head text-lg font-bold text-slate-900">Earn {money(data.settings.reward_signup_amount)} when you join</div>
          <div className="mt-1 text-xs text-slate-600">
            The Servantage team sets up a short demo. If you come on board, you get <strong>{money(data.settings.reward_signup_amount)}</strong> and {emp.name?.split(" ")[0]} gets rewarded too.
          </div>
        </div>

        {/* Form */}
        <div className="mt-3 rounded-2xl bg-white p-5 shadow-sm">
          <div className="font-head text-base font-bold text-slate-900">Your details</div>
          <p className="mt-0.5 text-xs text-slate-500">We'll only use these to set up your demo.</p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Your name <span className="text-rose-500">*</span></label>
              <Input value={form.referee_name} onChange={(e) => setForm({ ...form, referee_name: e.target.value })} data-testid="inv-name" required />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Your business <span className="text-rose-500">*</span></label>
              <Input value={form.referee_business} onChange={(e) => setForm({ ...form, referee_business: e.target.value })} data-testid="inv-business" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Your role</label>
                <Input value={form.referee_role} onChange={(e) => setForm({ ...form, referee_role: e.target.value })} placeholder="Waiter, Manager…" data-testid="inv-role" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Phone</label>
                <Input type="tel" inputMode="tel" value={form.referee_phone} onChange={(e) => setForm({ ...form, referee_phone: e.target.value })} data-testid="inv-phone" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Email</label>
              <Input type="email" inputMode="email" value={form.referee_email} onChange={(e) => setForm({ ...form, referee_email: e.target.value })} data-testid="inv-email" />
              <p className="mt-1 text-[11px] text-slate-400">Add at least a phone number or an email.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Anything the team should know? <span className="text-xs font-normal text-slate-400">(optional)</span></label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="min-h-[70px]" placeholder="Best time to reach you, what you'd love to see in a demo…" data-testid="inv-note" />
            </div>
          </div>
          <Button data-testid="inv-submit" disabled={busy} onClick={submit}
            className="mt-5 h-12 w-full rounded-full bg-[#4338CA] text-base hover:bg-[#3730A3] disabled:opacity-50">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {busy ? "Sending…" : "Send me the demo"}
          </Button>
          <p className="mt-3 text-center text-[11px] text-slate-400">By tapping Send, you agree we may contact you about Servantage.</p>
        </div>
      </div>
    </div>
  );
}
